import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  TruckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { adminService } from '../../services/adminService';
import { AdminDashboardStats, AdminOrder } from '../../types/admin';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [recentOrders, setRecentOrders] = useState<AdminOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { formatCurrency, convertPrice } = useLocale();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [statsResponse, ordersResponse] = await Promise.all([
        adminService.getAdminDashboard(),
        adminService.getRecentOrders(5),
      ]);

      // adminService methods already return the data payload
      setStats(statsResponse);
      setRecentOrders(ordersResponse);
    } catch (error: any) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'printing':
        return 'secondary';
      case 'printed':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-300 rounded w-1/4 mb-6' />
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6'>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className='h-32 bg-gray-300 rounded-lg' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className='text-center py-12'>
        <p className='text-gray-500'>Failed to load dashboard data</p>
        <Button onClick={loadDashboardData} className='mt-4'>
          Retry
        </Button>
      </div>
    );
  }

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  const statCards = [
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: ClipboardDocumentListIcon,
      color: 'blue',
      subtitle: `${stats.todayOrders} today`,
    },
    {
      title: 'Pending Orders',
      value: stats.pendingOrders,
      icon: ClockIcon,
      color: 'yellow',
      subtitle: 'Need attention',
    },
    {
      title: 'Processing',
      value: stats.processingOrders,
      icon: TruckIcon,
      color: 'purple',
      subtitle: 'In progress',
    },
    {
      title: 'Total Revenue',
      value: formatAmount(stats.totalRevenue),
      icon: BanknotesIcon,
      color: 'green',
      subtitle: `${formatAmount(stats.monthRevenue)} this month`,
    },
  ];

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Dashboard'
        description='Overview of store performance, recent orders, and key metrics.'
        url={generateCanonicalUrl('/admin')}
        type='website'
      />
      {/* Header */}
      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate'>
            Dashboard
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            Welcome back! Here's what's happening with your store today.
          </p>
        </div>
        <div className='mt-4 flex md:mt-0 md:ml-4'>
          <Button as={Link} to='/admin/orders' color='primary'>
            View All Orders
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6'>
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card key={index} className='border'>
              <CardBody className='p-6'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <div
                      className={`h-12 w-12 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}
                    >
                      <IconComponent
                        className={`h-6 w-6 text-${stat.color}-600`}
                      />
                    </div>
                  </div>
                  <div className='ml-4 flex-1'>
                    <div className='text-2xl font-bold text-gray-900'>
                      {stat.value}
                    </div>
                    <div className='text-sm font-medium text-gray-600'>
                      {stat.title}
                    </div>
                    <div className='text-xs text-gray-500'>{stat.subtitle}</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-medium text-gray-900'>
                Recent Orders
              </h3>
              <Button as={Link} to='/admin/orders' variant='ghost' size='sm'>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {recentOrders.length === 0 ? (
              <div className='text-center py-8 text-gray-500'>
                No recent orders
              </div>
            ) : (
              <div className='space-y-4'>
                {recentOrders.map(order => (
                  <div
                    key={order.id}
                    className='flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50'
                  >
                    <div className='flex-1'>
                      <div className='flex items-center justify-between'>
                        <Link
                          to={`/admin/orders/${order.id}`}
                          className='text-sm font-medium text-primary-600 hover:text-primary-700'
                        >
                          {order.orderNumber}
                        </Link>
                        <Chip
                          size='sm'
                          color={getStatusColor(order.status)}
                          variant='flat'
                        >
                          {order.status}
                        </Chip>
                      </div>
                      <div className='mt-1 text-sm text-gray-600'>
                        {order.shippingName} • {formatAmount(order.totalAmount)}
                      </div>
                      <div className='mt-1 text-xs text-gray-500'>
                        {new Date(order.orderDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <h3 className='text-lg font-medium text-gray-900'>Quick Actions</h3>
          </CardHeader>
          <CardBody>
            <div className='grid grid-cols-2 gap-4'>
              <Button
                as={Link}
                to='/admin/orders?status=Pending'
                color='warning'
                variant='flat'
                className='h-20 flex flex-col items-center justify-center'
              >
                <ClockIcon className='h-6 w-6 mb-2' />
                <span className='text-sm'>Pending Orders</span>
                <span className='text-xs font-bold'>{stats.pendingOrders}</span>
              </Button>
              <Button
                as={Link}
                to='/admin/orders?status=Processing'
                color='primary'
                variant='flat'
                className='h-20 flex flex-col items-center justify-center'
              >
                <TruckIcon className='h-6 w-6 mb-2' />
                <span className='text-sm'>Processing</span>
                <span className='text-xs font-bold'>
                  {stats.processingOrders}
                </span>
              </Button>
              <Button
                as={Link}
                to='/admin/email-settings'
                color='secondary'
                variant='flat'
                className='h-20 flex flex-col items-center justify-center'
              >
                <UsersIcon className='h-6 w-6 mb-2' />
                <span className='text-sm'>Customers</span>
                <span className='text-xs font-bold'>
                  {stats.totalCustomers}
                </span>
              </Button>
              <Button
                as={Link}
                to='/admin/analytics'
                color='success'
                variant='flat'
                className='h-20 flex flex-col items-center justify-center'
              >
                <ArrowTrendingUpIcon className='h-6 w-6 mb-2' />
                <span className='text-sm'>Analytics</span>
                <span className='text-xs'>View Reports</span>
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
